import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { EngagementSurveyOverviewPageDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import {
  type FormFiltersStorage,
  FormInsightsOverview,
} from 'modules/engagement_surveys/components/FormInsightsOverview'
import { ParticipationCard } from 'modules/engagement_surveys/components/ParticipationCard'
import { useSurveyActions } from 'modules/engagement_surveys/hooks/useSurveyActions'
import { useFormCycleSelection } from 'modules/forms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { SideContent } from './SideContent'

export default function SurveyOverviewPage() {
  const { surveyId } = useStrictParams<'surveyId'>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { data, loading, error } = useQuery(EngagementSurveyOverviewPageDocument, {
    variables: { surveyId: Number(surveyId) },
  })
  const survey = data?.surveys.survey
  const formId = survey?.form.id.toString() || ''
  const { handleActionClick } = useSurveyActions({ formId })
  const [savedStorage] = useLocalStorage<FormFiltersStorage | undefined>(
    LOCAL_STORAGE_KEYS.formResponseFilters(formId)
  )

  const { selectedCycleId } = useFormCycleSelection(survey, savedStorage ?? undefined)

  const materializedFormIds = useMemo(
    () => (selectedCycleId ? [selectedCycleId] : []),
    [selectedCycleId]
  )

  if (!survey) return null

  const hasQuestions =
    survey.form.elementsConnection?.totalCount && survey.form.elementsConnection.totalCount > 0

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      variant="fullscreen"
      onActionClick={handleActionClick}
    >
      <Box flexGrow height="full">
        <TwoColumnLayout sideContent={<SideContent survey={survey} />}>
          {hasQuestions ? (
            <FormInsightsOverview
              formId={survey.form.id.toString()}
              resourceType="Surveys::Survey"
              persistFilters
              survey={survey}
              headerContent={(activeFilters) => (
                <ParticipationCard
                  formId={formId}
                  materializedFormIds={materializedFormIds}
                  filters={activeFilters}
                  formEndsAt={survey.form.endsAt}
                  daysToAnswer={survey.form.configuration?.daysToAnswer}
                  status={survey.form.status}
                  shouldSkip={!survey || !formId || materializedFormIds.length === 0}
                />
              )}
            />
          ) : (
            <Box justifyContent="center" alignItems="center" height="full">
              <OneEmptyState
                emoji="✏️"
                title={i18n.t('forms.editor.empty_state.title')}
                description={i18n.t('forms.editor.empty_state.description')}
                actions={[
                  {
                    icon: Plus,
                    label: i18n.t('forms.editor.empty_state.button'),
                    onClick: () => {
                      navigate(
                        `${navigation.engagement.smart_surveys.survey.questions.fullPath({
                          surveyId,
                        })}?create=true`
                      )
                    },
                    variant: 'outline',
                  },
                ]}
              />
            </Box>
          )}
        </TwoColumnLayout>
      </Box>
    </ResponsivePage.Body>
  )
}
